// ===========================================
// Report Absence Dialog (Admin)
// ===========================================
// Admin-facing modal to report a student's absence on their behalf
// (e.g. when a parent calls in to report it manually)

import { useMemo, useState } from 'react';
import { format, isAfter, startOfDay } from 'date-fns';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AppLocalizationProvider } from '../../common/AppLocalizationProvider';
import {
  AccessTime as TimeIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EventBusy as AbsenceIcon,
  School as ClassIcon,
} from '@mui/icons-material';
import {
  useAffectedLessons,
  useCreateAbsence,
} from '../../../hooks/useAbsenceNotification';
import {
  AbsenceReason,
  ABSENCE_REASON_OPTIONS,
  getAbsenceReasonLabel,
} from '../../../api/absenceNotification.api';
import { parseUTCDate } from '../../../utils/dateUtils';

interface Props {
  open: boolean;
  studentId: string;
  studentName: string;
  onClose: () => void;
}

export default function ReportAbsenceDialog({
  open,
  studentId,
  studentName,
  onClose,
}: Props) {
  const [isDateRange, setIsDateRange] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reason, setReason] = useState<AbsenceReason | ''>('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  const createAbsence = useCreateAbsence();

  const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : '';
  const endDateStr = isDateRange && endDate
    ? format(endDate, 'yyyy-MM-dd')
    : startDateStr;

  const { data: affectedLessons, isLoading: lessonsLoading } = useAffectedLessons(
    studentId,
    startDateStr,
    endDateStr
  );

  const isFormValid = useMemo(() => {
    if (!startDate || !reason) return false;
    if (isDateRange && !endDate) return false;
    if (isDateRange && endDate && startDate && isAfter(startDate, endDate)) return false;
    if (reason === 'OTHER' && !reasonDetails.trim()) return false;
    return true;
  }, [startDate, endDate, isDateRange, reason, reasonDetails]);

  const minDate = startOfDay(new Date());

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const resetForm = () => {
    setIsDateRange(false);
    setStartDate(null);
    setEndDate(null);
    setReason('');
    setReasonDetails('');
    setShowSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!isFormValid || !startDate) return;

    const finalEndDate = isDateRange && endDate ? endDate : startDate;

    await createAbsence.mutateAsync({
      studentId,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(finalEndDate, 'yyyy-MM-dd'),
      reason: reason as AbsenceReason,
      reasonDetails: reasonDetails.trim() || undefined,
    });

    setShowSuccess(true);
  };

  return (
    <AppLocalizationProvider>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AbsenceIcon color="error" />
            <Typography variant="h6">Report Absence — {studentName}</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {showSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: 'success.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <CheckIcon sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Absence Notification Submitted
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {studentName}'s attendance has been updated and the school has been notified.
              </Typography>

              <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 2, textAlign: 'left', maxWidth: 480, mx: 'auto' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Student:</strong> {studentName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Date{isDateRange ? 's' : ''}:</strong>{' '}
                  {startDate && format(startDate, 'EEE, d MMM yyyy')}
                  {isDateRange && endDate && ` - ${format(endDate, 'EEE, d MMM yyyy')}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Reason:</strong> {reason && getAbsenceReasonLabel(reason as AbsenceReason)}
                  {reasonDetails && ` - ${reasonDetails}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Affected Classes:</strong> {affectedLessons?.length || 0}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
              {/* Form */}
              <Box sx={{ flex: 1 }}>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Submit an absence notification on behalf of the parent. Affected classes will be marked as absent automatically.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={isDateRange}
                      onChange={(e) => {
                        setIsDateRange(e.target.checked);
                        if (!e.target.checked) {
                          setEndDate(null);
                        }
                      }}
                    />
                  }
                  label="Multiple days"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <DatePicker
                    label={isDateRange ? 'Start Date' : 'Absence Date'}
                    value={startDate}
                    onChange={(date) => {
                      setStartDate(date);
                      if (!isDateRange) {
                        setEndDate(null);
                      }
                      if (date && endDate && isAfter(date, endDate)) {
                        setEndDate(null);
                      }
                    }}
                    minDate={minDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                      },
                    }}
                  />

                  {isDateRange && (
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      minDate={startDate || minDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                        },
                      }}
                    />
                  )}
                </Box>

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id="reason-label">Reason for Absence</InputLabel>
                  <Select
                    labelId="reason-label"
                    value={reason}
                    label="Reason for Absence"
                    onChange={(e) => setReason(e.target.value as AbsenceReason)}
                  >
                    {ABSENCE_REASON_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label={reason === 'OTHER' ? 'Please specify (required)' : 'Additional Details (optional)'}
                  value={reasonDetails}
                  onChange={(e) => setReasonDetails(e.target.value)}
                  multiline
                  rows={2}
                  required={reason === 'OTHER'}
                  placeholder={
                    reason === 'OTHER'
                      ? 'Please provide the reason for absence'
                      : 'Any additional information for the school'
                  }
                />

                {createAbsence.isError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    Failed to submit absence notification. Please try again.
                  </Alert>
                )}
              </Box>

              {/* Affected Classes Preview */}
              <Card sx={{ width: { xs: '100%', md: 320 }, alignSelf: 'flex-start' }} variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClassIcon color="primary" fontSize="small" />
                    Affected Classes
                  </Typography>

                  {!startDate ? (
                    <Typography color="text.secondary" variant="body2">
                      Select a date to see affected classes.
                    </Typography>
                  ) : lessonsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : !affectedLessons?.length ? (
                    <Alert severity="info" variant="outlined">
                      No scheduled classes during this period.
                    </Alert>
                  ) : (
                    <>
                      <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                        {affectedLessons.length} class{affectedLessons.length !== 1 ? 'es' : ''} will be marked as absent.
                      </Alert>

                      <List dense>
                        {affectedLessons.map((lesson, index) => (
                          <Box key={`${lesson.id}-${lesson.lessonDate}`}>
                            {index > 0 && <Divider />}
                            <ListItem sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <ClassIcon fontSize="small" color="action" />
                              </ListItemIcon>
                              <ListItemText
                                primary={lesson.name}
                                secondary={
                                  <Stack spacing={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                      {format(parseUTCDate(lesson.lessonDate), 'EEE, d MMM yyyy')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <TimeIcon sx={{ fontSize: 14 }} />
                                      <Typography variant="caption" color="text.secondary">
                                        {formatTime(lesson.startTime)} - {formatTime(lesson.endTime)}
                                      </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                      {lesson.teacher.firstName} {lesson.teacher.lastName}
                                      {lesson.instrument && ` - ${lesson.instrument.name}`}
                                    </Typography>
                                  </Stack>
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                              />
                            </ListItem>
                          </Box>
                        ))}
                      </List>
                    </>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {showSuccess ? (
            <Button variant="contained" onClick={handleClose}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleSubmit}
                disabled={!isFormValid || createAbsence.isPending}
                startIcon={createAbsence.isPending ? <CircularProgress size={18} color="inherit" /> : <AbsenceIcon />}
              >
                {createAbsence.isPending ? 'Submitting...' : 'Report Absence'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </AppLocalizationProvider>
  );
}
